import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import type { SchemaContext } from "src/jsonForms/types";
import cargoStage1 from "./schemas/cargo/stage1.json";
import cargoStage2 from "./schemas/cargo/stage2.json";
import cargoStage3 from "./schemas/cargo/stage3.json";

/**
 * Registry of JSON schemas keyed by LOB then stage identifier.
 * Override keys follow the convention `{stage}_{tpa}` or `{stage}_{country}`.
 *
 * To add a new LOB, add a new top-level key with its stage schemas.
 * To add a TPA/country override, add an entry like `stage1_sedgwick` or `stage1_CA`.
 */
export const SCHEMA_REGISTRY: Record<
  string,
  Record<string, CloudscapeJsonSchema>
> = {
  cargo: {
    stage1: cargoStage1 as unknown as CloudscapeJsonSchema,
    stage2: cargoStage2 as unknown as CloudscapeJsonSchema,
    stage3: cargoStage3 as unknown as CloudscapeJsonSchema,
  },
  // Future: wc: { ... }, ipl: { ... }
};

/**
 * Resolves the correct JSON schema for the given LOB, stage, and optional
 * TPA/country context. Lookup order:
 *
 * 1. TPA-specific override: `{stage}_{tpa}`
 * 2. Country-specific override: `{stage}_{country}`
 * 3. Base stage schema: `{stage}`
 *
 * Throws a descriptive error when the LOB or stage is not found.
 */
export function loadSchema(context: SchemaContext): CloudscapeJsonSchema {
  const lobSchemas = SCHEMA_REGISTRY[context.lob];
  if (!lobSchemas) {
    throw new Error(`No schemas found for LOB: ${context.lob}`);
  }

  // Check for TPA-specific override first
  if (context.tpa) {
    const overrideKey = `${context.stage}_${context.tpa}`;
    if (lobSchemas[overrideKey]) return lobSchemas[overrideKey];
  }

  // Check for country-specific override
  if (context.country) {
    const countryKey = `${context.stage}_${context.country}`;
    if (lobSchemas[countryKey]) return lobSchemas[countryKey];
  }

  // Fall back to base stage schema
  const schema = lobSchemas[context.stage];
  if (!schema) {
    throw new Error(`No schema found for ${context.lob}/${context.stage}`);
  }
  return schema;
}
